import {
  combineValidators,
  maxCharactersValidator,
  nonEmptyArrayValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import type { UpdateVideoRequest, VideoLibraryCategory, VideoLibraryItem } from "../../../api/types/videoLibraryTypes";

interface EditVideoForm {
  title: string;
  categories: VideoLibraryCategory[];
  uploadedAt: Date | null;
  description: string;
}

export const getInitialEditVideoForm = (
  editedVideo: VideoLibraryItem,
  allCategories: VideoLibraryCategory[]
): EditVideoForm => ({
  title: editedVideo.name,
  categories: allCategories.filter((category) => editedVideo.categoryIds.includes(category.id)).filter(Boolean),
  uploadedAt: editedVideo.uploadedAt ? new Date(editedVideo.uploadedAt) : null,
  description: editedVideo.description,
});

export const mapFormToUpdateRequest = (form: EditVideoForm): UpdateVideoRequest => ({
  title: form.title.trim(),
  descriptionUserInput: form.description.trim(),
  categoryIds: form.categories.map((category) => category.id),
  uploadedAt: form.uploadedAt ? form.uploadedAt.toISOString() : undefined,
});

type ValidationFieldKey = "title" | "categories";

interface FormValidationResult {
  isFormValid: boolean;
  validationErrors: Partial<Record<ValidationFieldKey, string>>;
}

export const validateForm = (form: EditVideoForm, existingVideoNames: string[]): FormValidationResult => {
  const validateTitle = combineValidators(
    requiredValidator,
    maxCharactersValidator(100),
    uniqueValidator("A video with such title already exists", existingVideoNames)
  );

  const validateCategories = nonEmptyArrayValidator("At least one category is required");

  const titleResult = validateTitle(form.title);
  const categoriesResult = validateCategories(form.categories);

  return {
    isFormValid: titleResult.isValid && categoriesResult.isValid,
    validationErrors: {
      title: titleResult.error,
      categories: categoriesResult.error,
    },
  };
};
